import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Menu, X, LogOut, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const Navbar = () => {
  const { user, isAuthenticated, hasAccess, login, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeLink, setActiveLink] = useState('HOME');
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Base navigation links (visible to everyone)
  const baseNavLinks = [
    { name: t('home'), href: '/#home' },
    { name: t('about'), href: '/#about' },
    { name: t('curriculum'), href: '/#curriculum' },
    { name: t('enroll'), href: '/#enroll' },
    { name: t('testimonials'), href: '/#testimonials' },
  ];

  // Add "THE COURSE" only if user is authenticated AND has paid access
  const navLinks = isAuthenticated && hasAccess
    ? [...baseNavLinks, { name: 'THE COURSE', href: '/course', isRoute: true, featured: true }]
    : baseNavLinks;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-[#1a1f2e]/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#d4a33a] rounded flex items-center justify-center">
              <Heart className="w-5 h-5 text-white fill-white" />
            </div>
            <span className="text-xl font-semibold">
              <span className="text-[#d4a33a]">Energy</span>
              <span className="text-white">Balance</span>
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              link.isRoute ? (
                <button
                  key={link.name}
                  onClick={() => { setActiveLink(link.name); navigate(link.href); }}
                  className={`text-sm font-medium tracking-wide transition-all duration-200 ${
                    link.featured 
                      ? 'px-4 py-2 bg-[#d4a33a]/20 text-[#d4a33a] rounded-lg border border-[#d4a33a]/50 hover:bg-[#d4a33a]/30'
                      : activeLink === link.name
                      ? 'text-[#d4a33a]'
                      : 'text-gray-300 hover:text-[#d4a33a]'
                  }`}
                >
                  {link.name}
                </button>
              ) : (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={() => setActiveLink(link.name)}
                  className={`text-sm font-medium tracking-wide transition-colors duration-200 ${
                    activeLink === link.name
                      ? 'text-[#d4a33a]'
                      : 'text-gray-300 hover:text-[#d4a33a]'
                  }`}
                >
                  {link.name}
                </a>
              )
            ))}
            
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 bg-[#232836] px-4 py-2 rounded-lg hover:bg-[#2a3142] transition-colors"
                >
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 bg-[#d4a33a] rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <span className="text-white text-sm">{user?.name?.split(' ')[0]}</span>
                </button>
                
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-[#232836] rounded-lg shadow-xl border border-gray-700 py-2">
                    <a
                      href="/dashboard"
                      className="flex items-center gap-2 px-4 py-2 text-gray-300 hover:bg-[#2a3142] hover:text-white"
                    >
                      <User className="w-4 h-4" />
                      {t('dashboard')}
                    </a>
                    <button
                      onClick={logout}
                      className="w-full flex items-center gap-2 px-4 py-2 text-red-400 hover:bg-red-500/10"
                    >
                      <LogOut className="w-4 h-4" />
                      {t('logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={login}
                className="bg-[#d4a33a] hover:bg-[#c49530] text-white px-5 py-2 rounded text-sm font-medium transition-colors duration-200"
              >
                {t('login')}
              </button>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-[#1a1f2e]/98 backdrop-blur-sm border-t border-gray-700">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              link.isRoute ? (
                <button
                  key={link.name}
                  onClick={() => {
                    setActiveLink(link.name);
                    setIsMobileMenuOpen(false);
                    navigate(link.href);
                  }}
                  className={`block w-full text-left text-sm font-medium tracking-wide py-2 ${
                    link.featured
                      ? 'px-4 py-3 bg-[#d4a33a]/20 text-[#d4a33a] rounded-lg border border-[#d4a33a]/50'
                      : activeLink === link.name
                      ? 'text-[#d4a33a]'
                      : 'text-gray-300'
                  }`}
                >
                  {link.name}
                </button>
              ) : (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={() => {
                    setActiveLink(link.name);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`block text-sm font-medium tracking-wide py-2 ${
                    activeLink === link.name
                      ? 'text-[#d4a33a]'
                      : 'text-gray-300'
                  }`}
                >
                  {link.name}
                </a>
              )
            ))}
            {isAuthenticated ? (
              <>
                <a
                  href="/dashboard"
                  className="block text-sm font-medium tracking-wide py-2 text-gray-300"
                >
                  {t('dashboard')}
                </a>
                <button
                  onClick={logout}
                  className="w-full bg-red-500/20 text-red-400 px-5 py-2.5 rounded text-sm font-medium mt-4"
                >
                  {t('logout')}
                </button>
              </>
            ) : (
              <button
                onClick={login}
                className="w-full bg-[#d4a33a] text-white px-5 py-2.5 rounded text-sm font-medium mt-4"
              >
                {t('login')}
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
